import { useEffect, useState } from 'react';
import { CalendarPlus, Loader2, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/app/layout/AppShell';
import { AttendanceSettings } from './AttendanceSettings';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { TimezonePicker } from '@/components/shared/TimezonePicker';
import { ApiError, api } from '@/lib/api';
import { useDeletedProjects, useUndeleteProject } from '@/features/projects/api';
import { indianHolidays, presetYears } from './indianHolidays';
import { useWorkspace, workspaceKey } from './api';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Recently deleted projects — V4 F-3.
 *
 * A soft delete is only reversible if someone can find it. Thirty days is the
 * window; after that the purge job takes it for good.
 */
function DeletedProjects() {
  const { data: deleted = [], error } = useDeletedProjects();
  const undelete = useUndeleteProject();

  // Not an admin: the endpoint 403s and this section simply is not for them.
  if (error || deleted.length === 0) return null;

  return (
    <div className="vt-surface mt-3 flex flex-col gap-2 p-4" data-testid="deleted-projects">
      <h2 className="text-[15px] font-semibold">Recently deleted projects</h2>
      <p className="text-[12px] vt-muted">
        Restorable for 30 days, with their tasks. After that they are purged.
      </p>

      <ul className="mt-1 flex flex-col gap-1.5">
        {deleted.map((project) => (
          <li
            key={project._id}
            className="flex items-center gap-2 text-[13px]"
            data-testid="deleted-project-row"
          >
            <span aria-hidden>{project.icon}</span>
            <span className="font-medium">{project.name}</span>
            <span className="vt-mono text-[11px] vt-muted">{project.key}</span>
            <span className="text-[11px] vt-muted">
              {project.taskCount} task{project.taskCount === 1 ? '' : 's'}
              {project.deletedBy ? ` · deleted by ${project.deletedBy}` : ''}
              {project.purgeAt
                ? ` · purged ${new Date(project.purgeAt).toLocaleDateString()}`
                : ''}
            </span>
            <Button
              variant="subtle"
              size="sm"
              className="ml-auto"
              data-testid="restore-project"
              disabled={undelete.isPending}
              onClick={() => {
                void undelete
                  .mutateAsync(project._id)
                  .then(() => toast.success(`${project.name} restored`))
                  .catch((err: unknown) =>
                    toast.error(err instanceof ApiError ? err.message : 'Could not restore'),
                  );
              }}
            >
              <RotateCcw />
              Restore
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** V4 F-5 — the shapes a working week actually takes here. */
const WORKING_DAY_PRESETS = [
  { id: 'mon-fri', label: 'Mon–Fri', days: [1, 2, 3, 4, 5] },
  { id: 'mon-sat', label: 'Mon–Sat', days: [1, 2, 3, 4, 5, 6] },
];

const sameDays = (a: number[], b: number[]): boolean =>
  a.length === b.length && [...a].sort().every((d, i) => d === [...b].sort()[i]);

const isCustom = (days: number[]): boolean =>
  !WORKING_DAY_PRESETS.some((p) => sameDays(days, p.days));

export function WorkspacePage() {
  const { data: workspace, isLoading } = useWorkspace();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [workingDays, setWorkingDays] = useState<number[]>([]);
  const [start, setStart] = useState('10:00');
  const [end, setEnd] = useState('19:00');
  const [holidays, setHolidays] = useState<{ date: string; name: string }[]>([]);
  const [defaultVisibility, setDefaultVisibility] = useState<'workspace' | 'private'>('private');
  // Next year by default: this is the list people load in December.
  const [presetYear] = useState(() => {
    const next = new Date().getUTCFullYear() + 1;
    return presetYears().includes(next) ? next : (presetYears().at(-1) ?? next);
  });

  useEffect(() => {
    if (!workspace) return;
    setName(workspace.name);
    setTimezone(workspace.timezone);
    setWorkingDays(workspace.workingDays);
    setStart(workspace.workingHours.start);
    setEnd(workspace.workingHours.end);
    setHolidays(workspace.holidays.map((h) => ({ date: h.date.slice(0, 10), name: h.name })));
    setDefaultVisibility(workspace.settings.defaultProjectVisibility);
  }, [workspace]);

  const save = useMutation({
    mutationFn: async () =>
      api.patch('/settings/workspace', {
        name: name.trim(),
        timezone,
        workingDays,
        workingHours: { start, end },
        holidays: holidays
          .filter((h) => h.date && h.name.trim())
          .map((h) => ({ date: new Date(`${h.date}T00:00:00.000Z`).toISOString(), name: h.name.trim() })),
        settings: { defaultProjectVisibility: defaultVisibility },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workspaceKey });
      toast.success('Workspace settings saved');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not save the workspace'),
  });

  if (isLoading || !workspace) {
    return (
      <>
        <PageHeader title="Workspace" />
        <div className="max-w-[560px] p-4">
          <Skeleton className="h-64" />
        </div>
      </>
    );
  }

  function toggleDay(day: number) {
    setWorkingDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort(),
    );
  }

  return (
    <>
      <PageHeader
        title="Workspace"
        subtitle="Working calendar drives every due-date, SLA and capacity calculation."
      />
      <div className="max-w-[620px] p-4">
        <div className="vt-surface flex flex-col gap-3 p-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ws-name">Workspace name</Label>
            <Input id="ws-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ws-tz">Timezone</Label>
            {/* V10 §4 — this is the value every attendance calculation runs
                through; it may not be free text. */}
            <TimezonePicker id="ws-tz" data-testid="ws-timezone" value={timezone} onChange={setTimezone} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Working days</Label>
            {/* V4 F-5 — the two shapes almost every Indian studio actually
                uses, with the day checkboxes still there for the rest. */}
            <div className="flex flex-wrap gap-1.5" data-testid="working-day-presets">
              {WORKING_DAY_PRESETS.map((preset) => {
                const active = sameDays(workingDays, preset.days);
                return (
                  <button
                    key={preset.label}
                    type="button"
                    aria-pressed={active}
                    data-testid={`preset-${preset.id}`}
                    onClick={() => setWorkingDays(preset.days)}
                    className={`h-7 rounded-[6px] border px-2.5 text-[12px] transition-colors duration-[120ms] ${
                      active
                        ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
                        : 'border-[var(--border)] text-[var(--text-muted)]'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
              <span className="self-center text-[11px] vt-muted">
                {isCustom(workingDays) ? 'Custom' : ''}
              </span>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {DAY_LABELS.map((label, day) => {
                const on = workingDays.includes(day);
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleDay(day)}
                    className={`h-7 rounded-[6px] border px-2.5 text-[12px] transition-colors duration-[120ms] ${
                      on
                        ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
                        : 'border-[var(--border)] text-[var(--text-muted)]'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] vt-muted">
              Overdue days, SLA counts and everyone’s capacity use working days only — changing
              this changes all three.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ws-start">Day starts</Label>
              <Input id="ws-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ws-end">Day ends</Label>
              <Input id="ws-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>
        </div>

        <DeletedProjects />

        {/* V4 D-1.5 — "open mode", stated as a plain choice rather than as a
            setting called `defaultProjectVisibility`. */}
        <div className="vt-surface mt-3 flex flex-col gap-2 p-4" data-testid="default-visibility">
          <h2 className="text-[15px] font-semibold">New projects are visible to</h2>
          <p className="text-[12px] vt-muted">
            This sets the default for projects created from now on. Projects that already exist
            keep the visibility they have — change those in each project’s settings.
          </p>

          <div className="mt-1 flex flex-col gap-2">
            {(
              [
                {
                  value: 'workspace' as const,
                  title: 'Everyone in the company',
                  hint: 'Simplest for a small team where nothing is confidential.',
                },
                {
                  value: 'private' as const,
                  title: 'Only project members (recommended)',
                  hint: 'Members, and anyone in a team linked to the project. Adding someone to a team gives them every project that team owns.',
                },
              ]
            ).map((option) => (
              <label
                key={option.value}
                className={`flex cursor-pointer gap-2.5 rounded-[8px] border p-2.5 transition-colors duration-[120ms] ${
                  defaultVisibility === option.value
                    ? 'border-[var(--accent)] bg-[var(--accent-soft)]'
                    : 'border-[var(--border)]'
                }`}
              >
                <input
                  type="radio"
                  name="default-visibility"
                  className="mt-0.5"
                  value={option.value}
                  checked={defaultVisibility === option.value}
                  onChange={() => setDefaultVisibility(option.value)}
                />
                <span className="flex flex-col gap-0.5">
                  <span className="text-[13px] font-medium">{option.title}</span>
                  <span className="text-[11px] vt-muted">{option.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="vt-surface mt-3 flex flex-col gap-2 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[15px] font-semibold">Holidays</h2>
              <p className="text-[11px] vt-muted">Removed from capacity and never counted as overdue days.</p>
            </div>
            <div className="flex gap-1.5">
              {/* V4 F-5 — one click for the list nobody wants to type twice.
                  Everything it adds is editable afterwards. */}
              <Button
                variant="secondary"
                size="sm"
                data-testid="load-indian-holidays"
                onClick={() => {
                  const preset = indianHolidays(presetYear);
                  setHolidays((prev) => {
                    const have = new Set(prev.map((h) => h.date));
                    const added = preset.filter((h) => !have.has(h.date));
                    if (added.length === 0) {
                      toast.info(`${presetYear} holidays are already loaded`);
                      return prev;
                    }
                    toast.success(`Added ${added.length} holidays — edit or remove any of them`);
                    return [...prev, ...added].sort((a, b) => a.date.localeCompare(b.date));
                  });
                }}
              >
                <CalendarPlus />
                Load Indian holidays {presetYear}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setHolidays((prev) => [...prev, { date: '', name: '' }])}
              >
                <Plus />
                Add
              </Button>
            </div>
          </div>

          {holidays.length === 0 ? (
            <p className="py-2 text-[12px] vt-muted">No holidays loaded for this year yet.</p>
          ) : (
            holidays.map((holiday, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="date"
                  className="w-[150px]"
                  value={holiday.date}
                  aria-label="Holiday date"
                  onChange={(e) =>
                    setHolidays((prev) =>
                      prev.map((h, i) => (i === index ? { ...h, date: e.target.value } : h)),
                    )
                  }
                />
                <Input
                  className="flex-1"
                  placeholder="Holiday name"
                  aria-label="Holiday name"
                  value={holiday.name}
                  onChange={(e) =>
                    setHolidays((prev) =>
                      prev.map((h, i) => (i === index ? { ...h, name: e.target.value } : h)),
                    )
                  }
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remove holiday"
                  className="text-[var(--danger)]"
                  onClick={() => setHolidays((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Trash2 />
                </Button>
              </div>
            ))
          )}
        </div>

        <div className="mt-3 flex justify-end">
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            Save workspace
          </Button>
        </div>

        {/* V6-04 §3 — saved separately, because these rules are re-read by a
            nightly job and a half-typed threshold must not reach it. */}
        <div className="mt-4">
          <AttendanceSettings />
        </div>
      </div>
    </>
  );
}
